import axios from "axios";
import { createSlice } from "@reduxjs/toolkit";
import { selectAuth } from "../auth/authSlice";
import { selectSelectedPlayer } from "../squad/squadSlice";
import { selectUser } from "../user/userSlice";
import { selectClub } from "../championship/championshipSlice";

let avatarsIDB;
const avatarsPlayersTable = "players";
const avatarsUsersTable = "users";
const avatarsClubsTable = "clubs";

export const initAvatarsIDB = () => {
  return new Promise((resolve) => {
    const openAvatarsIDBRequest = indexedDB.open("avatars");

    openAvatarsIDBRequest.onupgradeneeded = function () {
      avatarsIDB = openAvatarsIDBRequest.result;
      avatarsIDB.createObjectStore(avatarsPlayersTable, { keyPath: "pk" });
      avatarsIDB.createObjectStore(avatarsUsersTable, { keyPath: "pk" });
      avatarsIDB.createObjectStore(avatarsClubsTable, { keyPath: "pk" });
    };

    openAvatarsIDBRequest.onerror = function () {
      console.error("Impossible d'accéder à IndexedDB");
    };

    openAvatarsIDBRequest.onsuccess = function () {
      avatarsIDB = openAvatarsIDBRequest.result;
      resolve();
    };
  });
};

const getAvatarsIDB = (table, pk) => {
  return new Promise((resolve) => {
    const tx = avatarsIDB.transaction(table, "readwrite");
    const store = tx.objectStore(table);
    const request = store.get(pk);

    request.onsuccess = function () {
      resolve(request.result);
    };
  });
};

const putAvatarsIDB = (table, item) => {
  return new Promise((resolve) => {
    const tx = avatarsIDB.transaction(table, "readwrite");
    const store = tx.objectStore(table);
    const request = store.put(item);

    request.onsuccess = function () {
      resolve(request.result);
    };
  });
};

const getBlob = (picture, configType, etag) => async (dispatch, getState) => {
  const token = selectAuth(getState()).token.access;

  const getConfig = () => {
    if (configType === "read") {
      return {
        responseType: "blob",
        mode: "CORS",
        headers: {
          Authorization: `Bearer ${token}`,
          "If-None-Match": etag || null,
        },
      };
    } else if (configType === "update") {
      return {
        responseType: "blob",
        cache: "no-cache",
        mode: "CORS",
        headers: {
          Authorization: `Bearer ${token}`,
          "Cache-Purge": 1,
        },
      };
    }
  };

  const config = getConfig();

  return axios.get(picture, config).then((res) => ({
    blob: new File([res.data], ""),
    etag: res.headers.get("etag"),
  }));
};

export const getAvatarsPlayers = (players) => async (dispatch) => {
  players.forEach(async (player) => {
    const playerPk = player.pk;
    const playerPicture = player.picture;

    if (!playerPicture) {
      putAvatarsIDB(avatarsPlayersTable, {
        pk: playerPk,
        etag: null,
        avatar: null,
      });
      dispatch(photosActions.getAvatarPlayer({ pk: playerPk, avatar: null }));

      return;
    }

    const indexedPlayer = await getAvatarsIDB(avatarsPlayersTable, playerPk);

    return dispatch(getBlob(playerPicture, "read", indexedPlayer?.etag))
      .then((res) => {
        putAvatarsIDB(avatarsPlayersTable, {
          pk: playerPk,
          etag: res.etag,
          avatar: res.blob,
        });
        dispatch(
          photosActions.getAvatarPlayer({
            pk: playerPk,
            avatar: URL.createObjectURL(res.blob),
          })
        );
      })
      .catch(() => {
        dispatch(
          photosActions.getAvatarPlayer({
            pk: indexedPlayer?.pk || playerPk,
            avatar: indexedPlayer?.avatar
              ? URL.createObjectURL(indexedPlayer.avatar)
              : null,
          })
        );
      });
  });
};

export const setAvatarPlayer =
  (playerPicture) => async (dispatch, getState) => {
    const state = getState();
    const playerPk = selectSelectedPlayer(state).pk;

    return dispatch(getBlob(playerPicture, "update"))
      .then((res) => {
        putAvatarsIDB(avatarsPlayersTable, {
          pk: playerPk,
          etag: res.etag,
          avatar: res.blob,
        });
        dispatch(
          photosActions.setAvatarPlayer({
            pk: playerPk,
            avatar: URL.createObjectURL(res.blob),
          })
        );
      })
      .catch((err) => console.error(err));
  };

export const getAvatarUser = () => async (dispatch, getState) => {
  const state = getState();
  const userPk = selectUser(state).data.pk;
  const userPicture = selectUser(state).data.picture;

  if (!userPicture) {
    putAvatarsIDB(avatarsUsersTable, { pk: userPk, etag: null, avatar: null });
    dispatch(photosActions.getAvatarPlayer({ pk: userPk, avatar: null }));

    return;
  }

  const indexedUser = await getAvatarsIDB(avatarsUsersTable, userPk);

  return dispatch(getBlob(userPicture, "read", indexedUser?.etag))
    .then((res) => {
      putAvatarsIDB(avatarsUsersTable, {
        pk: userPk,
        etag: res.etag,
        avatar: res.blob,
      });
      dispatch(photosActions.getAvatarUser(URL.createObjectURL(res.blob)));
    })
    .catch(() =>
      dispatch(
        photosActions.getAvatarUser(
          indexedUser?.avatar ? URL.createObjectURL(indexedUser.avatar) : null
        )
      )
    );
};

export const setAvatarUser = () => async (dispatch, getState) => {
  const state = getState();
  const userPk = selectUser(state).data.pk;
  const userPicture = selectUser(state).data.picture;

  return dispatch(getBlob(userPicture, "update"))
    .then((res) => {
      putAvatarsIDB(avatarsUsersTable, {
        pk: userPk,
        etag: res.etag,
        avatar: res.blob,
      });
      dispatch(photosActions.getAvatarUser(URL.createObjectURL(res.blob)));
    })
    .catch((err) => console.error(err));
};

export const getAvatarClub = (clubPicture) => async (dispatch, getState) => {
  const clubPk = selectUser(getState()).data.club_id;

  if (!clubPicture) {
    putAvatarsIDB(avatarsClubsTable, { pk: clubPk, etag: null, avatar: null });
    dispatch(photosActions.getAvatarClub(null));

    return;
  }

  const indexedClub = await getAvatarsIDB(avatarsClubsTable, clubPk);

  return dispatch(getBlob(clubPicture, "read", indexedClub?.etag))
    .then((res) => {
      putAvatarsIDB(avatarsClubsTable, {
        pk: clubPk,
        etag: res.etag,
        avatar: res.blob,
      });
      dispatch(photosActions.getAvatarClub(URL.createObjectURL(res.blob)));
    })
    .catch(() =>
      dispatch(
        photosActions.getAvatarClub(
          indexedClub.avatar ? URL.createObjectURL(indexedClub.avatar) : null
        )
      )
    );
};

export const setAvatarClub = () => async (dispatch, getState) => {
  const state = getState();
  const clubPk = selectClub(state).id;
  const clubPicture = selectClub(state).logo;

  return dispatch(getBlob(clubPicture, "update"))
    .then((res) => {
      putAvatarsIDB(avatarsClubsTable, {
        pk: clubPk,
        etag: res.etag,
        avatar: res.blob,
      });
      dispatch(photosActions.getAvatarClub(URL.createObjectURL(res.blob)));
    })
    .catch((err) => console.error(err));
};

const initialState = {
  avatars: {
    players: [],
    user: null,
    club: null,
  },
};

const photosSlice = createSlice({
  name: "photos",
  initialState,
  reducers: {
    getAvatarPlayer: (state, action) => {
      state.avatars.players.push(action.payload);
    },
    setAvatarPlayer: (state, action) => {
      state.avatars.players = state.avatars.players.map((d) =>
        d.pk === action.payload.pk ? action.payload : d
      );
    },
    getAvatarUser: (state, action) => {
      state.avatars.user = action.payload;
    },
    getAvatarClub: (state, action) => {
      state.avatars.club = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase("auth/logout", () => initialState);
  },
});

export const photosActions = photosSlice.actions;

export const selectAvatarsPlayers = (state) => state.photos.avatars.players;

export const selectAvatarUser = (state) => state.photos.avatars.user;

export const selectAvatarClub = (state) => state.photos.avatars.club;

export default photosSlice.reducer;
